import { createFileRoute } from "@tanstack/react-router";
import { RestaurantCard } from "~/components/2_molecules/RestaurantCard";
import { SportCard } from "~/components/2_molecules/SportCard";
import { CarouselVariable } from "~/components/3_organisms/CarouselVariable";
import { Hero } from "~/components/4_modules/Hero";
import { ImageBanner } from "~/components/4_modules/ImageBanner";
import StandardSection from "~/components/4_modules/StandardSection";
import { SEO } from "~/components/SEO";
import {
  localBusinessSchema,
  organizationSchema,
  sportsFacilitySchema,
} from "~/lib/structured-data";

const homeContent = {
  hero: {
    backgroundImage: "/images/home-hero.webp",
    logo: {
      src: "/images/tennis-logo.webp",
      alt: "Tennis Gourmet",
      className: "w-auto h-[40%] md:h-[50%] 2xl:h-[50%]",
    },
    title: "Dove il gusto incontra lo sport",
  },
  welcome: {
    title: {
      mobile: "Benvenuti al <br /> Ristorante Tennis & Gourmet",
      desktop: "Benvenuti al Ristorante Tennis & Gourmet",
    },
    paragraph: [
      "Nel cuore della Valtellina, il Tennis & Gourmet è più di un semplice ristorante: è un luogo dove la passione per la cucina si fonde con l'amore per lo sport e l'accoglienza familiare.",
      "Che siate alla ricerca di un pranzo raffinato nel nostro ristorante, di una pizza gourmet o di un momento di relax a bordo piscina, qui troverete un’esperienza unica.",
    ],
  },
  restaurant: {
    title: "Il Ristorante",
    paragraph: [
      "In una terra rinomata per i suoi piatti di montagna, abbiamo scelto di proporreuna cucina prevalentemente di pesce.",
      "Selezioniamo quotidianamente materie prime freschissime, trasformandole in piatti che esaltano i sapori del marecon creatività e rispetto per la tradizione.",
    ],
  },
  sport: {
    title: "Sport e relax",
    paragraph: [
      "Il Tennis & Gourmet non è solo ristorante, ma anche un centro dedicato al benessere e allo sport. La nostra piscina è un’oasi di relax dove rinfrescarsi durante la bella stagione, ideale per famiglie e gruppidi amici.",
      "I campi da Tennis, Padel e Calcetto sono strutture moderne e ben mantenute per gli amanti dello sport. ",
    ],
  },
};

export const Route = createFileRoute("/")({
  component: Home,
});

function Home() {
  const combinedSchema = {
    "@context": "https://schema.org",
    "@graph": [localBusinessSchema, organizationSchema, sportsFacilitySchema],
  };

  return (
    <>
      <SEO
        title="Home"
        socialTitle="Ristorante Tennis & Gourmet Morbegno | Home"
        description="Ristorante Morbegno con piscina e padel in Valtellina. Tennis Gourmet: cucina di pesce, pizzeria gourmet, centro sportivo con piscina Morbegno e padel Valtellina. Prenota ora!"
        keywords="ristorante morbegno, ristorante valtellina, piscina morbegno, piscina valtellina, padel valtellina, tennis morbegno, pizzeria gourmet morbegno, centro sportivo valtellina, ristorante pesce morbegno"
        url="https://www.ristorantetennisgourmet.it/"
        structuredData={combinedSchema}
      />
      {/* Hero Section */}
      <Hero backgroundImage={homeContent.hero.backgroundImage}>
        <img
          src={homeContent.hero.logo.src}
          alt={homeContent.hero.logo.alt}
          className={homeContent.hero.logo.className}
        />
        <h2 className="tg-h2">{homeContent.hero.title}</h2>
      </Hero>
      {/* Welcome Section */}
      <StandardSection
        title={homeContent.welcome.title}
        paragraph={homeContent.welcome.paragraph}
      ></StandardSection>
      {/* Carousel Variable */}
      <CarouselVariable
        images={[
          {
            src: "/images/home-carousel/01.webp",
            alt: "Interno elegante del ristorante Tennis Gourmet con tavoli apparecchiati",
          },
          {
            src: "/images/home-carousel/02.webp",
            alt: "Piatto gourmet di pesce fresco servito al ristorante",
          },
          {
            src: "/images/home-carousel/03.webp",
            alt: "Campo da tennis del centro sportivo Tennis Gourmet",
          },
          {
            src: "/images/home-carousel/04.webp",
            alt: "Piscina del centro Tennis Gourmet per il relax estivo",
          },
          {
            src: "/images/home-carousel/05.webp",
            alt: "Pizza gourmet con ingredienti freschi e impasto artigianale",
          },
          {
            src: "/images/home-carousel/06.webp",
            alt: "Campo da padel moderno e ben attrezzato",
          },
          {
            src: "/images/home-carousel/07.webp",
            alt: "Ambiente accogliente del bar con vista sui campi sportivi",
          },
          {
            src: "/images/home-carousel/08.webp",
            alt: "Terrazza esterna del ristorante con vista panoramica sulla Valtellina",
          },
          {
            src: "/images/home-carousel/09.webp",
            alt: "Cucina professionale del ristorante Tennis Gourmet",
          },
        ]}
        className="my-8"
      />
      {/* Restaurant Section */}
      <StandardSection
        title={homeContent.restaurant.title}
        paragraph={homeContent.restaurant.paragraph}
        decors={[
          <img
            key="decor-1"
            src="/images/decor/decor-01R.webp"
            alt="page decoration"
            className="absolute top-1/4 right-0 h-auto w-[40vw] object-cover md:top-1/6 md:w-[30vw] xl:hidden"
          />,
        ]}
        children={[
          <RestaurantCard
            key="ristorante-1"
            imageSrc="/images/home-ristorante.webp"
            imageAlt="Ristorante"
            title="Ristorante"
            href="/restaurant/#restaurant-section"
          />,
          <RestaurantCard
            key="ristorante-2"
            imageSrc="/images/home-vino.webp"
            imageAlt="Vino"
            title="Vino"
          />,
          <RestaurantCard
            key="ristorante-3"
            imageSrc="/images/home-pizzeria.webp"
            imageAlt="Pizzeria"
            title="Pizzeria"
            href="/restaurant/#pizzeria"
          />,
        ]}
      ></StandardSection>
      {/* Image Banner */}
      <ImageBanner
        src={{
          mobile: "/images/banner-external-restaurant--mobile.webp",
          desktop: "/images/banner-external-restaurant.webp",
        }}
        alt="External picture of the restaurant"
      />
      {/* Sport & relax Section */}
      <StandardSection
        title={homeContent.sport.title}
        paragraph={homeContent.sport.paragraph}
        decors={[
          <img
            key="decor-1"
            src="/images/decor/decor-01R.webp"
            alt="page decoration"
            className="absolute top-2/6 right-0 h-auto w-[40vw] object-cover md:w-[30vw] xl:top-0 xl:w-[15vw]"
          />,
          <img
            key="decor-2"
            src="/images/decor/decor-01L.webp"
            alt="page decoration"
            className="absolute top-3/6 left-0 h-auto w-[40vw] object-cover md:w-[30vw] xl:top-0 xl:hidden xl:w-[15vw]"
          />,
          <img
            key="decor-3"
            src="/images/decor/decor-01R.webp"
            alt="page decoration"
            className="absolute top-4/6 right-0 h-auto w-[40vw] object-cover md:w-[30vw] xl:top-0 xl:hidden xl:w-[15vw]"
          />,
        ]}
        children={[
          <SportCard
            key="sport-1"
            href="/sport#piscina"
            imageSrc="/images/icons/icon-piscina.webp"
            imageAlt="Piscina"
            title="Piscina"
          />,
          <SportCard
            key="sport-2"
            href="/sport#campo-da-tennis"
            imageSrc="/images/icons/icon-tennis.webp"
            imageAlt="Tennis"
            title={
              <>
                Campi
                <br />
                da tennis
              </>
            }
          />,
          <SportCard
            key="sport-3"
            href="/sport#campo-da-padel"
            imageSrc="/images/icons/icon-padel.webp"
            imageAlt="Padel"
            title={
              <>
                Campi
                <br />
                da padel
              </>
            }
          />,
          <SportCard
            key="sport-4"
            href="/sport#biliardo"
            imageSrc="/images/icons/icon-biliardo.webp"
            imageAlt="Biliardo"
            title="Biliardo"
          />,
        ]}
      ></StandardSection>
    </>
  );
}
